import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EVENTS, EventData, Joyride, Locale, Options, PartialDeep, Step, Styles } from "react-joyride";
import { CHANGELOG_CLOSED_EVENT, isChangeLogPending } from "../ChangeLogDialog";
import { FRAMING_STEP_IDS, IntroTourStep } from "./introTourSteps";

const INTRO_TOUR_STORAGE_KEY = "gtr_intro_tour_seen";
const START_DELAY_MS = 1000;
const MENU_TRANSITION_MS = 350;

const storageKey = (userId: number) => `${INTRO_TOUR_STORAGE_KEY}_${userId}`;

const readSeenSteps = (userId: number): string[] => {
    try {
        const parsed = JSON.parse(localStorage.getItem(storageKey(userId)) ?? "[]");
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const saveSeenSteps = (userId: number, ids: string[]) => {
    try {
        const seen = new Set([...readSeenSteps(userId), ...ids]);
        localStorage.setItem(storageKey(userId), JSON.stringify([...seen]));
    } catch {
        // Sem localStorage o tour volta a aparecer no próximo acesso.
    }
};

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const LOCALE: Locale = {
    back: "Voltar",
    close: "Fechar",
    last: "Concluir",
    next: "Próximo",
    open: "Abrir",
    skip: "Pular tour",
};

const OPTIONS: Partial<Options> = {
    buttons: ["back", "skip", "primary"],
    skipBeacon: true,
    // Clicar no item destacado navegaria para fora do rastreamento.
    blockTargetInteraction: true,
    overlayClickAction: false,
    dismissKeyAction: false,
    primaryColor: "#006C77",
    textColor: "#1f2937",
    spotlightRadius: 10,
    spotlightPadding: 6,
    targetWaitTimeout: 300,
    width: 340,
    // Acima dos botões do mapa (99999) e da grade em tela cheia (100000).
    zIndex: 100500,
};

const STYLES: PartialDeep<Styles> = {
    tooltip: { borderRadius: 14, fontFamily: "Prometo" },
    tooltipTitle: { fontSize: 17, fontWeight: 700, color: "#09414A" },
    tooltipContent: { fontSize: 14, lineHeight: 1.5 },
    buttonPrimary: { borderRadius: 8, fontFamily: "Prometo" },
    buttonBack: { fontFamily: "Prometo", color: "#006C77" },
    buttonSkip: { fontFamily: "Prometo" },
};

type UseIntroTourOptions = {
    userId: number;
    /** Tela e usuário elegíveis neste momento. Ficar falso interrompe o tour sem marcar como visto. */
    enabled: boolean;
    steps: IntroTourStep[];
    setMenuOpen: (open: boolean) => void;
};

type UseIntroTourResult = {
    tour: ReactElement | null;
    running: boolean;
    restart: () => void;
};

/**
 * Tour de introdução do rastreamento.
 * Abre sozinho enquanto houver passos não vistos pelo usuário (localStorage, chave + userId).
 * Quem já fez o tour vê só os passos novos, sem a abertura e o encerramento.
 */
export const useIntroTour = ({ userId, enabled, steps, setMenuOpen }: UseIntroTourOptions): UseIntroTourResult => {
    const [activeSteps, setActiveSteps] = useState<IntroTourStep[] | null>(null);
    const [runId, setRunId] = useState(0);
    const stepsRef = useRef(steps);
    const setMenuOpenRef = useRef(setMenuOpen);
    const menuOpenRef = useRef<boolean | null>(null);
    const autoStartedRef = useRef(false);

    useEffect(() => {
        stepsRef.current = steps;
        setMenuOpenRef.current = setMenuOpen;
    });

    const start = useCallback((onlyPending: boolean) => {
        const seen = onlyPending ? readSeenSteps(userId) : [];
        const selected = seen.length === 0
            ? stepsRef.current
            : stepsRef.current.filter((step) => !seen.includes(step.id) && !FRAMING_STEP_IDS.includes(step.id));
        if (!selected.some((step) => !FRAMING_STEP_IDS.includes(step.id))) return;

        menuOpenRef.current = null;
        setActiveSteps(selected);
        setRunId((value) => value + 1);
    }, [userId]);

    useEffect(() => {
        if (!enabled || autoStartedRef.current || userId <= 0) return;
        let timer: number | undefined;
        const begin = () => {
            timer = window.setTimeout(() => {
                autoStartedRef.current = true;
                start(true);
            }, START_DELAY_MS);
        };
        // Não disputa a tela com o diálogo de novidades.
        if (isChangeLogPending()) window.addEventListener(CHANGELOG_CLOSED_EVENT, begin, { once: true });
        else begin();
        return () => {
            window.removeEventListener(CHANGELOG_CLOSED_EVENT, begin);
            window.clearTimeout(timer);
        };
    }, [enabled, userId, start]);

    useEffect(() => {
        if (!enabled) setActiveSteps(null);
    }, [enabled]);

    const ensureMenu = useCallback(async (open: boolean) => {
        if (menuOpenRef.current === open) return;
        menuOpenRef.current = open;
        setMenuOpenRef.current(open);
        await wait(MENU_TRANSITION_MS);
    }, []);

    const joyrideSteps = useMemo<Step[]>(() => (activeSteps ?? []).map((step) => ({
        id: step.id,
        title: step.title,
        content: step.content,
        placement: step.placement ?? "auto",
        target: step.placement === "center" ? "body" : `[data-tour="${step.id}"]`,
        before: () => ensureMenu(!!step.inMenu),
    })), [activeSteps, ensureMenu]);

    const handleEvent = useCallback((data: EventData) => {
        if (data.type === EVENTS.TOUR_END) {
            // Passos pulados por falta de alvo (sem permissão, funcionalidade desligada) também contam como vistos;
            // senão o tour reabriria a cada acesso só para pulá-los.
            saveSeenSteps(userId, (activeSteps ?? []).map((step) => step.id));
            setActiveSteps(null);
            if (menuOpenRef.current) setMenuOpenRef.current(false);
        }
    }, [activeSteps, userId]);

    const tour = activeSteps ? (
        <Joyride
            key={runId}
            run
            continuous
            steps={joyrideSteps}
            onEvent={handleEvent}
            locale={LOCALE}
            options={OPTIONS}
            styles={STYLES}
        />
    ) : null;

    const restart = useCallback(() => start(false), [start]);

    return { tour, running: activeSteps !== null, restart };
};
